import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { ThemeProvider } from "@/lib/ThemeContext";
import Layout from "@/components/Layout";
import WeeklyPlan from "@/pages/WeeklyPlan";
import BakingSection from "@/pages/BakingSection";
import Recipes from "@/pages/Recipes";
import RecipeDetail from "@/pages/RecipeDetail";
import ShoppingList from "@/pages/ShoppingList";
import PantryPage from "@/pages/PantryPage";
import WasteReport from "@/pages/WasteReport";
import CustomLists from "@/pages/CustomLists";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import PWAManifestSwitcher from "@/components/PWAManifestSwitcher";
import TripsPage from "@/pages/TripsPage";
import ReceiptScanPage from "@/pages/ReceiptScanPage";
import StatistikkPage from "@/pages/StatistikkPage";
import BoatsPage from "@/pages/BoatsPage";
import MessagesPage from "@/pages/MessagesPage";
import AdminHealthPage from "@/pages/AdminHealthPage";
import CatalogPage from "@/pages/CatalogPage";
import OnboardingPage from "@/pages/OnboardingPage";
import HomePage from "@/pages/HomePage";
import HouseholdPlan from "@/pages/HouseholdPlan";
import AdminOverview from "@/pages/AdminOverview";
import AdminUsers from "@/pages/AdminUsers";
import AdminFeedback from "@/pages/AdminFeedback";
import HomeAdmin from "@/pages/HomeAdmin";
import CalendarPage from "@/pages/CalendarPage";
import { Toaster } from "@/components/ui/sonner";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-navy-800 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Redirect to onboarding if user hasn't been onboarded
  if (!user.onboarded && window.location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return children;
}

function ProtectedRouteNoOnboard({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-navy-800 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarded) return <Navigate to="/" replace />;
  return children;
}

function HomeRedirect({ children }) {
  const { activeBoat } = useAuth();
  if (activeBoat?.kind === "household") return <Navigate to="/home" replace />;
  return children;
}

/** Guard — Home Edition-sider (/home, /admin/home, /household-plan) skal kun
 *  være tilgjengelige når aktiv båt er en husstand. Hvis båt er aktiv, send til / */
function HouseholdOnly({ children }) {
  const { activeBoat } = useAuth();
  if (activeBoat && activeBoat.kind !== "household") return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const [servings, setServings] = useState(6);
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <PWAManifestSwitcher />
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={
        <ProtectedRouteNoOnboard><OnboardingPage /></ProtectedRouteNoOnboard>
      } />
      <Route
        element={
          <ProtectedRoute>
            <Layout servings={servings} setServings={setServings} />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<HomeRedirect><WeeklyPlan servings={servings} /></HomeRedirect>} />
        <Route path="/home" element={<HouseholdOnly><HomePage /></HouseholdOnly>} />
        <Route path="/baking" element={<BakingSection servings={servings} />} />
        <Route path="/recipes" element={<Recipes servings={servings} />} />
        <Route path="/recipes/:id" element={<RecipeDetail />} />
        <Route path="/pantry" element={<PantryPage />} />
        <Route path="/shopping" element={<ShoppingList servings={servings} />} />
            <Route path="/my-lists" element={<CustomLists />} />
            <Route path="/waste" element={<WasteReport />} />
            <Route path="/trips" element={<TripsPage />} />
            <Route path="/scan" element={<ReceiptScanPage />} />
            <Route path="/statistikk" element={<StatistikkPage />} />
            <Route path="/boats" element={<BoatsPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/admin/health" element={<AdminHealthPage />} />
            <Route path="/admin/overview" element={<AdminOverview />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/feedback" element={<AdminFeedback />} />
            <Route path="/admin/home" element={<HouseholdOnly><HomeAdmin /></HouseholdOnly>} />
            <Route path="/household-plan" element={<HouseholdOnly><HouseholdPlan /></HouseholdOnly>} />
            <Route path="/calendar" element={<HouseholdOnly><CalendarPage /></HouseholdOnly>} />
            <Route path="/catalog" element={<CatalogPage />} />
      </Route>
    </Routes>
    </>
  );
}

function App() {
  return (
    <>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </BrowserRouter>
        <Toaster />
      </ThemeProvider>
    </>
  );
}

export default App;
